import React, { PropsWithChildren } from 'react'
import { observer } from 'mobx-react-lite'

import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import type { SxProps } from '@mui/material/styles'

import { ChevronLeft } from '@mxenabled/mx-icons'
import { Icon, Text } from '@mxenabled/mxui'

import { ANALYTICS_EVENTS, useEvent } from '../../common'
import { AccountType } from '../../core'
import {
  useAppConfig,
  useGlobalCopyStore,
  useGlobalUiStore,
  WidgetContainerProvider,
} from '../context'
import { useWidgetHeaderStyles } from '../hooks'
import { useDimensions } from '../hooks/useDimensions'

import { CalendarActions } from './daterangeselector/DateRangeSelector'
import { GlobalAccountFilter } from './GlobalAccountFilter'
import { WidgetDateRangeSelector } from './WidgetDateRangeSelector'
import Loader from './Loader'

type Action = {
  label: string
  iconName: string
  onClick: () => void
}

interface WidgetContainerProps {
  allowedAccountTypes?: AccountType[] | undefined
  actions?: (Action | React.JSX.Element)[]
  calendarActions?: CalendarActions
  className?: string
  dateRange?: { start: Date; end: Date }
  dateRangeVariant?: 'outlined' | 'text' | 'readonly' | 'timeframebuttons' | 'timeframetabs'
  hasDivider?: boolean
  onBackClick?: () => void
  onMenuClick?: () => void
  onAccountsFilterClick?: () => void
  showAccountFilter?: boolean
  slotComponent?: React.ReactElement
  sx?: SxProps
  title?: string
}

export const WidgetContainer: React.FC<PropsWithChildren<WidgetContainerProps>> = observer(
  ({
    actions,
    allowedAccountTypes,
    calendarActions,
    children,
    className = 'mx-cmn-widget',
    dateRange,
    dateRangeVariant,
    hasDivider = false,
    onAccountsFilterClick,
    onBackClick,
    onMenuClick,
    showAccountFilter = true,
    slotComponent,
    sx = {},
    title = '',
  }) => {
    const [containerRef, { height, width }] = useDimensions()
    const [contentRef, { height: contentHeight, width: contentWidth }] = useDimensions()
    const { onEvent } = useEvent()

    const {
      actionsFlex,
      containerSquareCorners,
      headerHeadingLevel,
      headerPaddingBottom,
      headerPaddingTop,
      headerPaddingX,
      iconSize,
      isCompact,
      isDesktop,
      isViewportAbove900,
      slotSx,
    } = useWidgetHeaderStyles(width)
    const { isCopyLoaded } = useGlobalUiStore()
    const { common: commonCopy } = useGlobalCopyStore()
    const {
      config: { display_widget_headings: showTitle },
    } = useAppConfig()

    const handleBackClick = () => {
      if (onBackClick) {
        onBackClick()
        onEvent(ANALYTICS_EVENTS.WIDGET_BACK_CLICK)
      }
    }

    React.useEffect(() => {
      onEvent(ANALYTICS_EVENTS.WIDGET_SIZE_CHANGED, {
        height: contentHeight,
        width: contentWidth,
      })
    }, [contentHeight, contentWidth])

    if (!isCopyLoaded) {
      return <Loader />
    }

    const getNavButton = () => {
      if (onMenuClick && isCompact && !isViewportAbove900) {
        return (
          <IconButton color="primary" onClick={onMenuClick} sx={{ p: 10 }}>
            <Icon name="menu_open" size={iconSize} sx={{ transform: 'rotate(180deg)' }} />
          </IconButton>
        )
      }

      if (onBackClick) {
        return (
          <IconButton
            aria-label={commonCopy.back_to_dashboard}
            color="primary"
            onClick={handleBackClick}
            sx={{ p: 10 }}
          >
            <ChevronLeft size={24} />
          </IconButton>
        )
      }

      return null
    }

    const defaultSlotComponent = calendarActions && (
      <Stack alignItems="center" sx={slotSx}>
        <WidgetDateRangeSelector
          calendarActions={calendarActions}
          dateRange={dateRange}
          dateRangeVariant={dateRangeVariant}
        />
      </Stack>
    )

    return (
      <Paper
        className={className}
        elevation={1}
        ref={containerRef}
        square={containerSquareCorners}
        sx={{
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
          margin: 0,
          overflowY: 'auto',
          height: '100%',
          ...sx,
        }}
      >
        <Card>
          <Stack
            alignItems="center"
            alignSelf="stretch"
            direction="row"
            justifyContent="space-between"
            sx={{
              pb: headerPaddingBottom,
              pt: headerPaddingTop,
              px: headerPaddingX,
            }}
          >
            {getNavButton()}
            <Stack alignItems="flex-start" minWidth={0} sx={{ flex: 1 }}>
              {showTitle && (
                <Text sx={{ width: '100%' }} truncate={true} variant={headerHeadingLevel}>
                  {title}
                </Text>
              )}
            </Stack>
            {!isCompact && (slotComponent ? slotComponent : defaultSlotComponent)}
            <Stack
              alignItems="center"
              direction="row"
              justifyContent="flex-end"
              sx={{ flex: actionsFlex, flexShrink: 0 }}
            >
              {showAccountFilter && (
                <GlobalAccountFilter
                  allowedAccountTypes={allowedAccountTypes}
                  isDesktop={isDesktop}
                  onAccountFilterClick={onAccountsFilterClick}
                />
              )}
              {actions?.map((action) => {
                if (React.isValidElement(action))
                  return React.cloneElement(action, { isDesktop } as Partial<unknown>)
                const { iconName, onClick, label } = action as Action
                return isDesktop ? (
                  <Button
                    key={iconName}
                    onClick={onClick}
                    startIcon={<Icon name={iconName} size={iconSize} />}
                  >
                    {label}
                  </Button>
                ) : (
                  <IconButton aria-label={label} color="primary" key={iconName} onClick={onClick}>
                    <Icon name={iconName} size={iconSize} />
                  </IconButton>
                )
              })}
            </Stack>
          </Stack>
          {isCompact && (slotComponent ? slotComponent : defaultSlotComponent)}
          {isCompact && hasDivider && <Divider />}
          <CardContent ref={contentRef} sx={{ p: 0 }}>
            <WidgetContainerProvider availableHeight={height} availableWidth={width}>
              {children}
            </WidgetContainerProvider>
          </CardContent>
        </Card>
      </Paper>
    )
  },
)
